import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Search, GitBranch, ArrowDownRight, ArrowUpRight } from "lucide-react";

export default function Traceability() {
  const { data: batches = [] } = useQuery({
    queryKey: ["batches-all"],
    queryFn: async () => {
      const r = await api.get("/reports/aging");
      // reuse aging to get batch info per container
      const seen = new Set();
      return r.data
        .filter(x => { if (seen.has(x.batch_number)) return false; seen.add(x.batch_number); return true; })
        .map(x => ({ batch_number: x.batch_number, item: x.item, days_in_stock: x.days_in_stock }));
    },
  });
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const { data: trace } = useQuery({
    queryKey: ["trace", selected],
    queryFn: async () => {
      // find batch_id from batch_number using ledger/aging
      const all = await api.get("/reports/current-stock");
      // fallback: fetch all batches by querying activity for batch resolution isn't ideal; use containers
      const containers = await api.get("/containers?include_emptied=true");
      const c = containers.data.find(x => x.code.startsWith(selected + "/"));
      if (!c) return null;
      const t = await api.get(`/trace/batch/${c.batch_id}`);
      return t.data;
    },
    enabled: !!selected,
  });

  const filtered = batches.filter(b =>
    !q || b.batch_number.toLowerCase().includes(q.toLowerCase())
    || b.item.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="trace-page">
      <div>
        <div className="data-label">Provenance</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Traceability</h1>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-1">
          <div className="relative mb-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input className="field-sm pl-9" placeholder="Filter batches"
                   value={q} onChange={(e) => setQ(e.target.value)}
                   data-testid="trace-search" />
          </div>
          <ul className="bg-white border border-border rounded-sm divide-y divide-border max-h-[70vh] overflow-auto">
            {filtered.map(b => (
              <li key={b.batch_number}>
                <button onClick={() => setSelected(b.batch_number)}
                        className={`w-full text-left p-3 row-hover ${selected === b.batch_number ? "bg-primary/5" : ""}`}
                        data-testid={`trace-select-${b.batch_number}`}>
                  <div className="container-code text-primary">{b.batch_number}</div>
                  <div className="text-xs text-muted-foreground">{b.item} · {b.days_in_stock}d</div>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-2">
          {!selected && <div className="text-sm text-muted-foreground p-6 bg-white border border-border rounded-sm">Pick a batch to trace.</div>}
          {selected && trace && (
            <div className="bg-white border border-border rounded-sm p-4 space-y-4" data-testid="trace-detail">
              <div>
                <div className="container-code text-primary">{trace.batch.batch_number}</div>
                <div className="text-sm text-muted-foreground">{trace.item_name}</div>
                {trace.production_order && (
                  <span className="badge-flat bg-secondary/15 text-secondary mt-2">
                    <GitBranch className="w-3 h-3 mr-1" /> Produced by {trace.production_order.order_number}
                  </span>
                )}
              </div>
              {trace.input_batches.length > 0 && (
                <div>
                  <div className="data-label mb-2 flex items-center gap-1">
                    <ArrowDownRight className="w-3 h-3" /> Consumed from
                  </div>
                  <ul className="space-y-2">
                    {trace.input_batches.map(ib => (
                      <li key={ib.batch_id} className="border-l-2 border-primary/30 pl-3">
                        <div className="container-code">{ib.batch_number}</div>
                        <div className="text-xs text-muted-foreground">
                          {ib.item_name} · {ib.supplier_name} · received {ib.received_date}
                        </div>
                        <div className="font-mono text-sm">Used: {ib.qty_consumed}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {trace.consumed_by.length > 0 && (
                <div>
                  <div className="data-label mb-2 flex items-center gap-1">
                    <ArrowUpRight className="w-3 h-3" /> Used in production
                  </div>
                  <ul className="space-y-2">
                    {trace.consumed_by.map(c => (
                      <li key={c.order_number} className="border-l-2 border-secondary/30 pl-3">
                        <div className="container-code">{c.order_number}</div>
                        <div className="text-xs text-muted-foreground">
                          → {c.output_batch_number || "—"} · {c.completed_at ? new Date(c.completed_at).toLocaleDateString() : ""}
                        </div>
                        <div className="font-mono text-sm">Consumed: {c.qty_consumed}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {trace.input_batches.length === 0 && trace.consumed_by.length === 0 && (
                <div className="text-sm text-muted-foreground italic">
                  This is a raw batch not yet used in any production.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
